import { useFetchMembers } from 'app/components/members/useMemberActions';
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import { AuthContext } from './AuthContext';
import WorkspaceContext from './WorkspaceContext';
import { useFetchTeams } from 'app/components/members/useTeamActions';
import { useFetchJoinRequests } from 'app/[locale]/w/[workspaceId]/components/member/useWorkspaceMemberStore';
import { useMemberStore } from 'app/components/members/MemberStore';
import { useFetchRoles } from 'app/[locale]/w/[workspaceId]/components/role/useRoleActions';

interface Props {
  children: ReactNode;
}

export const MembersContext = createContext<undefined>(undefined);

export const MembersContextProvider: React.FC<PropsWithChildren<Props>> = ({ children }) => {
  const { authState } = useContext(AuthContext);
  const { workspace } = useContext(WorkspaceContext);
  const fetchMembers = useFetchMembers();
  const fetchTeams = useFetchTeams();
  const fetchJoinRequests = useFetchJoinRequests();
  const fetchRoles = useFetchRoles();
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const refetchAllMemberData = useCallback(async () => {
    if (!workspace || authState.type !== 'signedIn') return;
    // Promise.all로 되어있었으나, 중간에 refresh가 호출될 경우 순차처리가 필요해서 다시 변경.
    await fetchMembers();
    await fetchTeams(authState.callaboApiClient, workspace);
    await fetchJoinRequests(authState.callaboApiClient);
  }, [authState.type, workspace, fetchMembers, fetchTeams, fetchJoinRequests]);

  useEffect(() => {
    // Store the refetch function in the MemberStore
    useMemberStore.setState({ refetch: refetchAllMemberData });

    // Initial fetch
    refetchAllMemberData();
  }, [refetchAllMemberData]);

  return <MembersContext.Provider value={undefined}>{children}</MembersContext.Provider>;
};
